import { Field, Fieldset, Label } from "@headlessui/react";
import { CheckIcon } from "@heroicons/react/20/solid";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { api } from "../api";
import logo from "../assets/logo.png";
import { Gradient } from "./Gradient";
import { Button } from "./tailwind/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "./tailwind/dialog";
import { Input } from "./tailwind/input";
import { Text } from "./tailwind/text";

export const SignInPage = ({ setShowSignUp, handleSignIn, isLoading }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [errors, setErrors] = useState({
    email: "",
    password: "",
    newPassword: "",
  });
  const [modalEmail, setModalEmail] = useState(""); // Email de la modale
  const [modalErrors, setModalErrors] = useState({ email: "" });
  const [isModalResetPasswordOpen, setIsModalResetPasswordOpen] =
    useState(false);
  const [isModalTokenErrorOpen, setIsModalTokenErrorOpen] = useState(false);
  const [user, setUser] = useState(null);

  const urlParams = new URLSearchParams(window.location.search);

  const [resetPasswordToken] = useState(
    urlParams.get("reset_password_token") ?? "",
  );

  useEffect(() => {
    (async () => {
      if (resetPasswordToken !== "") {
        try {
          const userRecord = await api.findUserByResetPasswordToken({
            resetPasswordToken,
          });
          console.log("userRecord", userRecord);
          if (userRecord) {
            setUser(userRecord);
            setIsModalResetPasswordOpen(true);
            setNewPassword("");
          } else {
            setIsModalTokenErrorOpen(true);
          }
        } catch {
          setIsModalTokenErrorOpen(true);
        }
      }
    })();
  }, [
    resetPasswordToken,
    setIsModalResetPasswordOpen,
    setIsModalTokenErrorOpen,
  ]);

  const validate = async () => {
    const newErrors = { email: "", password: "" };

    if (!email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Email address is invalid";
    } else {
      try {
        const userRecordId = await api.findUserIdByEmail({ email });
        if (!userRecordId) {
          throw new Error("User not found");
        }
      } catch (error) {
        console.error(error);
        newErrors.email = "User not found";
      }
    }

    if (!password) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);
    // Retourne true si il n'y a pas d'erreurs
    return !newErrors.email && !newErrors.password;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isValid = await validate();

    if (isValid) {
      try {
        const result = await handleSignIn({
          email: email.toLowerCase().trim(),
          password,
        });

        if (!result) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            password: "Incorrect password",
          }));
        } else if (result.isBlocked) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            password: "Your account is blocked, please contact the support.",
          }));
        }
      } catch {
        setErrors((prevErrors) => ({
          ...prevErrors,
          password: "An unexpected error occurred. Please try again.",
        }));
      }
    }
  };

  const sendResetPasswordEmail = async (email) => {
    try {
      const user = await api.findUserIdByEmail({
        email,
      });
      if (user) {
        await api.user.sendResetPassword(user.id);
        setIsModalOpen(true);
      } else {
        setModalErrors({ email: "User not found" });
      }
    } catch (error) {
      console.error("Error sending reset password email:", error);
      setModalErrors({ email: "Failed to send reset password email" });
    }
  };

  const changePassword = async (emailToUse) => {
    const newModalErrors = { email: "" };
    if (!emailToUse) {
      newModalErrors.email = "Email is required";
      setModalErrors(newModalErrors);
      setIsModalEmailOpen(true);
    } else if (!/\S+@\S+\.\S+/.test(emailToUse)) {
      newModalErrors.email = "Email address is invalid";
      setModalErrors(newModalErrors);
      setIsModalEmailOpen(true);
    } else {
      setModalErrors(newModalErrors);
      setIsModalEmailOpen(false);
      try {
        await sendResetPasswordEmail(emailToUse);
        setIsModalOpen(true);
      } catch {
        setModalErrors((prevErrors) => ({
          ...prevErrors,
          email: "Failed to send reset password email",
        }));
      }
    }
  };

  const saveNewPassword = async () => {
    const newModalErrors = { newPassword: "" };
    if (newPassword === "") {
      newModalErrors.newPassword = "Password is required";
      setModalErrors(newModalErrors);
    } else {
      setModalErrors(newModalErrors);
      console.log("reset password");
      try {
        await api.user.resetPassword(user.id, {
          password: newPassword,
          resetPasswordToken: resetPasswordToken,
        });
      } catch (error) {
        console.error(
          "Erreur lors de l'appel à l'API de réinitialisation du mot de passe :",
          error,
        );
      }
      setIsModalResetPasswordOpen(false);
      await api.user.emailSignIn(user.id, {
        email: user.email,
        password: newPassword,
      });
    }
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalEmailOpen, setIsModalEmailOpen] = useState(false);

  const handleResetPasswordClick = () => {
    setModalEmail(email);
    setIsModalEmailOpen(true);
  };

  return (
    <>
      <Gradient className="h-full">
        <div className="flex min-h-full z-0 flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8 absolute w-full">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <img className="mx-auto h-10 w-auto" src={logo} alt="Simio" />
            <h2 className="mt-6 text-center text-2xl font-bold font-title leading-9 tracking-tight text-gray-900">
              Sign in to your account
            </h2>
          </div>

          <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px] z-10">
            <div className="bg-white px-6 py-5 shadow sm:rounded-lg sm:px-12">
              <form className="space-y-6 relative z-10" onSubmit={handleSubmit}>
                <Fieldset>
                  <Field>
                    <Label>Email address</Label>
                    <Input
                      name="email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      invalid={!!errors.email}
                      aria-errormessage="email-error"
                    />
                    {errors.email && (
                      <p id="email-error" className="text-red-500 text-xs mt-1">
                        {errors.email}
                      </p>
                    )}
                  </Field>
                  <br />
                  <Field>
                    <div className="mt-1 flex justify-between w-full text-xs">
                      <Label>Password</Label>
                      <Link
                        onClick={handleResetPasswordClick}
                        className="m-0 text-yellow"
                      >
                        Reset password
                      </Link>
                    </div>
                    <Input
                      name="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      invalid={!!errors.password}
                      aria-errormessage="password-error"
                    />
                    {errors.password && (
                      <p
                        id="password-error"
                        className="text-red-500 text-xs mt-1"
                      >
                        {errors.password}
                      </p>
                    )}
                  </Field>
                </Fieldset>

                <Button
                  type="submit"
                  color="primaryButton"
                  className="flex w-full justify-center bg-yellow font-title font-bold leading-6"
                  loading={isLoading}
                >
                  Sign in
                </Button>
              </form>

              {/* Divider */}
              <div className="mt-6">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">or</span>
                  </div>
                </div>
              </div>

              {/* Microsoft Sign In Button */}
              <div className="mt-6">
                <Button
                  type="button"
                  onClick={() =>
                    (window.location.href = "/azure/login?selectAccount=true")
                  }
                  className="flex w-full justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg
                    className="w-5 h-5 mr-2"
                    viewBox="0 0 21 21"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                    <rect x="12" y="1" width="9" height="9" fill="#7FBA00" />
                    <rect x="1" y="12" width="9" height="9" fill="#00A4EF" />
                    <rect x="12" y="12" width="9" height="9" fill="#FFB900" />
                  </svg>
                  Sign in with Microsoft
                </Button>
              </div>
            </div>

            <Dialog
              key="modal-success"
              open={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              className="z-50"
            >
              <DialogTitle>Email sent successfully</DialogTitle>
              <DialogBody>
                <div className="rounded-full w-[8em] h-[8em] bg-yellow m-auto pt-2 mb-6">
                  <CheckIcon className="h-[7em] m-auto" />
                </div>
                <Text className="text-base font-bold text-center">
                  {`We sent you an email at ${modalEmail}. Please follow the link to change your password.`}
                </Text>
              </DialogBody>
              <DialogActions>
                <Button
                  color="primaryButton"
                  onClick={() => setIsModalOpen(false)}
                >
                  OK
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog
              key="modal-email"
              open={isModalEmailOpen}
              onClose={() => setIsModalEmailOpen(false)}
              className="z-50"
            >
              <DialogTitle>
                You will receive a link to reset your password
              </DialogTitle>
              <DialogBody>
                <Field>
                  <Label>Email address</Label>
                  <Input
                    name="email"
                    type="email"
                    value={modalEmail}
                    onChange={(e) => setModalEmail(e.target.value)}
                    aria-invalid={!!modalErrors.email}
                    aria-errormessage="modal-email-error"
                  />
                  {modalErrors.email && (
                    <p
                      id="modal-email-error"
                      className="text-red-500 text-xs mt-1"
                    >
                      {modalErrors.email}
                    </p>
                  )}
                </Field>
              </DialogBody>
              <DialogActions>
                <Button
                  color="primaryButton"
                  onClick={() => changePassword(modalEmail)}
                >
                  Change my password
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog
              key="token-error"
              open={isModalTokenErrorOpen}
              onClose={() => setIsModalTokenErrorOpen(false)}
              className="z-50"
            >
              <DialogTitle>Outdated link</DialogTitle>
              <DialogBody>
                <Field>
                  <Label>
                    This link is no longer available. Please ask for a new one
                    to change your password.
                  </Label>
                </Field>
              </DialogBody>
              <DialogActions>
                <Button
                  color="primaryButton"
                  onClick={() => {
                    setIsModalEmailOpen(true);
                    setIsModalTokenErrorOpen(false);
                  }}
                >
                  Reset password
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog
              key="modal-reset-password"
              open={isModalResetPasswordOpen}
              onClose={() => setIsModalResetPasswordOpen(false)}
              className="z-50"
            >
              <DialogTitle>Enter your new password</DialogTitle>
              <DialogBody>
                <Field>
                  <Label>New password</Label>
                  <Input
                    name="new-password"
                    type="password"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    aria-invalid={!!modalErrors.newPassword}
                    aria-errormessage="modal-email-error"
                  />
                  {modalErrors.newPassword && (
                    <p
                      id="modal-email-error"
                      className="text-red-500 text-xs mt-1"
                    >
                      {modalErrors.newPassword}
                    </p>
                  )}
                </Field>
              </DialogBody>
              <DialogActions>
                <Button color="primaryButton" onClick={() => saveNewPassword()}>
                  Change my password
                </Button>
              </DialogActions>
            </Dialog>

            <Text className="mt-10 text-center">
              Not a member?{" "}
              <Link
                onClick={() => setShowSignUp(true)}
                className="font-semibold text-purple hover:text-purple/80"
              >
                Sign up instead
              </Link>
            </Text>
          </div>
        </div>
        <div className="relative z-50"></div>
      </Gradient>
    </>
  );
};
